import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  CircularProgress,
  Grid,
} from '@mui/material';
import dayjs from 'dayjs';
import {useFormik} from 'formik';
import update from 'immutability-helper';
import _, {isEmpty} from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';

import API from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import useShiftSelect, {isShiftClick} from '../../../hooks/useShiftSelect';
import {
  GeneralQueryParams,
  NetworkDiagnosticsGeneralItem,
  NetworkDiagnosticsGeneralResponse,
} from '../../../interfaces/CommunicationNode';
import {ExportField} from '../../../interfaces/Export';
import reduxSelectors from '../../../redux/selectors';
import DataGrid, {DataGridColumn, DataGridRef} from '../../common/DataGrid';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
import {SectionMultipleSelect} from '../../selectors/SectionSelect';
import NetworkDiagnosticsGeneralExportButton from './NetworkDiagnosticsGeneralExportButton';

interface Props {
  value?: NetworkDiagnosticsGeneralData;
  onUpdate?: (value?: NetworkDiagnosticsGeneralData) => void;
}

const DEFAULT_SHOWN_FIELDS = [
  'external_id',
  'name',
  'heartbeats_lost',
  'message_interval',
  'zero_parent_count',
  'network_entry_total',
  'resets',
  'idle_0_count',
  'idle_1_count',
  'idle_2_count',
  'idle_3_count',
];

export interface NetworkDiagnosticsGeneralData {
  params: GeneralQueryParams;
  selectedIds?: number[];
  exportFields: ExportField[];
  columnWidths: Record<string, number>;
  columnOrder?: string[];
  shownFields?: string[];
}

export const getNetworkDiagnosticsGeneralData =
  (): NetworkDiagnosticsGeneralData => {
    return {
      params: {
        date_start: dayjs().format('YYYY-MM-DD'),
        date_end: dayjs().format('YYYY-MM-DD'),
        limit: 10,
        page: 0,
        order: 'external_id',
        dir: 'ASC',
        section_ids: undefined,
      },
      selectedIds: undefined,
      exportFields: [],
      columnWidths: {},
      columnOrder: undefined,
      shownFields: DEFAULT_SHOWN_FIELDS,
    };
  };

type NetworkDiagnosticsGeneralGridRow = NetworkDiagnosticsGeneralItem;

export const NetworkDiagnosticsGeneral = ({value, onUpdate}: Props) => {
  const isDarkMode = useSelector(reduxSelectors.app.getIsDarkMode);

  const config = useMemo<NetworkDiagnosticsGeneralData>(
    () => (!isEmpty(value) ? value : getNetworkDiagnosticsGeneralData()),
    [value]
  );

  /*********/
  /* fetch */
  /*********/
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);
  const [fetchedData, setFetchedData] =
    useState<NetworkDiagnosticsGeneralResponse>();

  const fetchData = async (params: GeneralQueryParams) => {
    setFetchedInProgress(true);
    try {
      const endpoint = `${apiBaseUrl}/communication-node/network-general-grid/`;
      const resp = await API.get<NetworkDiagnosticsGeneralResponse>(endpoint, {
        params,
      });
      setFetchedData(resp.data);
    } catch (error: any) {
      const errors = error.response.data?.message
        ? [error.response.data?.message]
        : [];
      setFetchedErrors(errors);
    }
    setFetchedInProgress(false);
  };

  const params = useMemo<GeneralQueryParams>(
    () => ({
      date_start: config?.params?.date_start ?? dayjs().format('YYYY-MM-DD'),
      date_end: config?.params?.date_end ?? dayjs().format('YYYY-MM-DD'),
      page: config.params.page ?? 0,
      limit: config?.params?.limit ?? 10,
      order: config?.params?.order ?? 'external_id',
      dir: config?.params?.dir ?? 'ASC',
      section_ids: config?.params?.section_ids ?? undefined,
    }),
    [config?.params]
  );

  const formik = useFormik({
    initialValues: params,
    onSubmit: (v) => {
      onUpdate?.(
        update(config, {
          params: {$set: v},
        })
      );
    },
  });

  const handleFetchData = async () => {
    await fetchData(params);
  };

  useEffect(() => {
    if (!_.isEqual(params, formik.values)) {
      formik.setValues(params);
    }
    handleFetchData();
  }, [params]);

  useEffect(() => {
    if (!_.isEqual(params, formik.values)) {
      formik.handleSubmit();
    }
  }, [formik.values]);

  /*************/
  /* data grid */
  /*************/
  const dataGridRef = useRef<DataGridRef>(null);

  const shownFields = useMemo(
    () => config?.shownFields ?? DEFAULT_SHOWN_FIELDS,
    [config?.shownFields]
  );

  const rows = useMemo<NetworkDiagnosticsGeneralGridRow[]>(
    () => fetchedData?.items ?? [],
    [fetchedData]
  );

  const handleShownFieldsChange = useCallback(
    (fields: string[]) => {
      onUpdate?.(
        update(config, {
          shownFields: {$set: fields},
          exportFields: {
            $set: collectExportFields(fields),
          },
        })
      );
    },
    [onUpdate, config]
  );

  const handleColumnOrderAndShownFieldsChange = useCallback(
    (order: string[], fields: string[]) => {
      onUpdate?.(
        update(config, {
          columnOrder: {$set: order},
          shownFields: {$set: fields},
          exportFields: {
            $set: collectExportFields(fields),
          },
        })
      );
    },
    [onUpdate, config]
  );

  const collectExportFields = (fields?: string[]) => {
    return columns
      .filter((col) => !!col.headerName)
      .map((col) => ({
        field: col.field,
        label: col.headerName,
        hidden: !!fields && !fields.includes(col.field),
      }));
  };

  /*******************/
  /* multiple select */
  /*******************/

  const [newSelectedItemIds, setNewSelectedItemIds] = useState(
    value?.selectedIds ?? []
  );

  const columns: DataGridColumn<NetworkDiagnosticsGeneralGridRow>[] = useMemo(
    () => [
      {
        headerName: '',
        field: 'select',
        type: 'select',
        width: 60,
        sortable: true,
        renderHeader: () => (
          <Checkbox
            color="primary"
            disabled={fetchedData?.items.length === 0}
            indeterminate={
              newSelectedItemIds.length > 0 &&
              newSelectedItemIds.length < rows.length
            }
            checked={
              rows.length > 0 && newSelectedItemIds.length === rows.length
            }
            onChange={toogleSelectAllItemIds}
          />
        ),
        renderCell: ({row, visibleRows}) => (
          <Checkbox
            color="primary"
            checked={newSelectedItemIds.includes(row.external_id)}
            onChange={(event) =>
              toogleSelectItemId(
                row.external_id,
                isShiftClick(event),
                visibleRows.map((visibleRow) => visibleRow.external_id)
              )
            }
          />
        ),
      },
      {
        field: 'external_id',
        headerName: 'Network ID',
        width: 120,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => +row.external_id,
      },
      {
        field: 'name',
        headerName: 'Name',
        width: 120,
        resizeable: true,
        sortable: true,
      },
      {
        field: 'heartbeats_lost',
        headerName: 'Heartbeats Lost',
        width: 100,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => +row.heartbeats_lost,
      },
      {
        field: 'message_interval',
        headerName: 'Message Interval',
        width: 100,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => +row.message_interval,
      },
      {
        field: 'zero_parent_count',
        headerName: 'Zero Parent Count',
        width: 100,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => +row.zero_parent_count,
      },
      {
        field: 'network_entry_total',
        headerName: 'Network Entry Total',
        width: 120,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => +row.network_entry_total,
      },
      {
        field: 'resets',
        headerName: 'Resets',
        width: 60,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => +row.resets,
      },
      {
        field: 'idle_0_count',
        headerName: 'Idle 0 Count',
        width: 80,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => +row.idle_0_count,
      },
      {
        field: 'idle_1_count',
        headerName: 'Idle 1 Count',
        width: 80,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => +row.idle_1_count,
      },
      {
        field: 'idle_2_count',
        headerName: 'Idle 2 Count',
        width: 80,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => +row.idle_2_count,
      },
      {
        field: 'idle_3_count',
        headerName: 'Idle 3 Count',
        width: 80,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => +row.idle_3_count,
      },
    ],
    [newSelectedItemIds, fetchedData, rows]
  );

  const toogleSelectAllItemIds = () => {
    if (newSelectedItemIds?.length === rows.length) {
      setNewSelectedItemIds([]);
    } else {
      setNewSelectedItemIds(rows.map((_i) => _i.external_id));
    }
  };

  const getNextSelectedIds = useShiftSelect<number>();

  const toogleSelectItemId = (
    id: number,
    shiftKey: boolean,
    visibleIds: number[]
  ) => {
    const nextSelectedIds = getNextSelectedIds({
      id,
      visibleIds,
      selectedIds: newSelectedItemIds ?? [],
      shiftKey,
    });
    setNewSelectedItemIds(nextSelectedIds);
  };

  useEffect(() => {
    const newValue = newSelectedItemIds ?? [];
    if (!_.isEqual(newValue, value?.selectedIds)) {
      onUpdate?.(
        update(value, {
          selectedIds: {
            $set: newValue,
          },
        })
      );
    }
  }, [newSelectedItemIds, value]);

  useEffect(() => {
    const newValue = value?.selectedIds ?? [];
    if (!_.isEqual(newSelectedItemIds, newValue)) {
      setNewSelectedItemIds?.(newValue);
    }
  }, [value?.selectedIds]);

  const handleColumnWidthChange = (widths: Record<string, number>) => {
    onUpdate?.(
      update(config, {
        columnWidths: {$set: widths},
      })
    );
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      overflow="hidden"
      width="100%"
    >
      <Grid container spacing={1} p={2} minWidth={800}>
        <Grid item xs={4}>
          <DateRangeSelect
            value={
              value
                ? [
                    dayjs(value?.params?.date_start).toDate(),
                    dayjs(value?.params?.date_end).toDate(),
                  ]
                : undefined
            }
            size="small"
            disabled={fetchedInProgress}
            onChange={(value) => {
              formik.setFieldValue(
                'date_start',
                dayjs(value[0] ?? undefined).format('YYYY-MM-DD')
              );
              formik.setFieldValue(
                'date_end',
                dayjs(value[1] ?? undefined).format('YYYY-MM-DD')
              );
            }}
          />
        </Grid>

        <Grid item xs={4}>
          <SectionMultipleSelect
            value={formik.values.section_ids}
            size="small"
            onChange={(v) => formik.setFieldValue('section_ids', v)}
          />
        </Grid>

        <Grid item xs={4} display="flex" justifyContent="end">
          <ButtonGroup>
            <Button size="small">
              <NetworkDiagnosticsGeneralExportButton
                params={params}
                exportFields={
                  config.exportFields.length > 0
                    ? config.exportFields
                    : collectExportFields()
                }
              />
            </Button>

            <Button size="small" onClick={handleFetchData}>
              <RefreshIcon />
            </Button>

            <Button
              size="small"
              onClick={() => dataGridRef.current?.printTable()}
            >
              <PrintIcon />
            </Button>
          </ButtonGroup>
        </Grid>
      </Grid>

      <Backdrop
        open={fetchedInProgress}
        sx={{
          position: 'absolute',
          zIndex: 1001,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {fetchedErrors.map((error, index) => (
        <Alert key={index} severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
      ))}

      <DataGrid
        ref={dataGridRef}
        rows={rows}
        columns={columns}
        loading={fetchedInProgress}
        size="small"
        pagination
        paginationMode="server"
        sortingMode="server"
        rowCount={fetchedData?.count ?? params.page * params.limit}
        page={formik.values.page}
        pageSize={formik.values.limit}
        shownFields={shownFields}
        defaultShownFields={DEFAULT_SHOWN_FIELDS}
        columnWidths={config.columnWidths}
        columnOrder={config.columnOrder}
        onPageChange={(v) => formik.setFieldValue('page', v)}
        onPageSizeChange={(v) => {
          formik.setFieldValue('limit', v);
          formik.setFieldValue('page', 0);
        }}
        onShownFieldsChange={handleShownFieldsChange}
        onColumnWidthsChange={handleColumnWidthChange}
        onColumnOrderAndShownFieldsChange={handleColumnOrderAndShownFieldsChange}
        onSort={(v) => {
          formik.setFieldValue('order', v?.field);
          formik.setFieldValue('dir', v?.dir.toUpperCase());
        }}
        sxFooter={{bgcolor: isDarkMode ? '#2E2E2E' : '#FFF'}}
      />
    </Box>
  );
};
